// ── Enum / Union Types ──────────────────────────────────────────────────────

export type KanbanListRole =
  | 'backlog'
  | 'commitment_point'
  | 'in_progress'
  | 'delivery_point'
  | 'done'
  | 'none';

export type KanbanCardType =
  | 'bug'
  | 'feature'
  | 'task'
  | 'story'
  | 'improvement'
  | 'epic'
  | 'subtask'
  | 'other';

export type KanbanClassOfService =
  | 'expedite'
  | 'fixed_date'
  | 'standard'
  | 'intangible';

export type KanbanCadenceType =
  | 'standup'
  | 'review'
  | 'retrospective'
  | 'planning'
  | 'replenishment'
  | 'custom';

export type KanbanCadenceFrequency =
  | 'daily'
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'custom';

export type KanbanCardLinkType =
  | 'note'
  | 'warning'
  | 'ban'
  | 'incident'
  | 'calendar_event'
  | 'ticket'
  | 'partnership'
  | 'partnership_creator'
  | 'partnership_modpack'
  | 'build'
  | 'build_project'
  | 'build_team_member'
  | 'event'
  | 'event_project'
  | 'event_team_member'
  | 'modpack_note'
  | 'player'
  | 'evidence'
  | 'kanban_card'
  | 'appeal';

// ── Constant Arrays ─────────────────────────────────────────────────────────

export const KANBAN_LIST_ROLES: KanbanListRole[] = [
  'backlog',
  'commitment_point',
  'in_progress',
  'delivery_point',
  'done',
  'none',
];

export const KANBAN_CARD_TYPES: KanbanCardType[] = [
  'bug',
  'feature',
  'task',
  'story',
  'improvement',
  'epic',
  'subtask',
  'other',
];

export const KANBAN_CLASSES_OF_SERVICE: KanbanClassOfService[] = [
  'expedite',
  'fixed_date',
  'standard',
  'intangible',
];

export const KANBAN_CADENCE_TYPES: KanbanCadenceType[] = [
  'standup',
  'review',
  'retrospective',
  'planning',
  'replenishment',
  'custom',
];

export const KANBAN_CADENCE_FREQUENCIES: KanbanCadenceFrequency[] = [
  'daily',
  'weekly',
  'biweekly',
  'monthly',
  'custom',
];

export const KANBAN_LINK_TYPES: KanbanCardLinkType[] = [
  'note',
  'warning',
  'ban',
  'incident',
  'calendar_event',
  'ticket',
  'partnership',
  'partnership_creator',
  'partnership_modpack',
  'build',
  'build_project',
  'build_team_member',
  'event',
  'event_project',
  'event_team_member',
  'modpack_note',
  'player',
  'evidence',
  'kanban_card',
  'appeal',
];

// ── Core Entity Interfaces ──────────────────────────────────────────────────

export interface KanbanBoardPermissions {
  read: boolean;
  write: boolean;
  manage: boolean;
  manage_access: boolean;
}

export interface KanbanBoardAccess {
  staff_teams: string[];
  staff_users: string[];
  can_manage: boolean;
}

export interface KanbanBoardAccessOptions {
  staff_teams: { id: string; name: string; display_name: string; domain: string }[];
  staff_users: { minecraft_uuid: string; teams: string[]; username?: string }[];
}

export interface KanbanBoard {
  id: string;
  team: string;
  title: string;
  description: string | null;
  is_default: number; // 0 | 1
  background_color: string | null;
  is_archived: number; // 0 | 1
  created_at: number;
  created_by: string;
  updated_at: number | null;
  updated_by: string | null;
  permissions?: KanbanBoardPermissions;
}

export interface KanbanList {
  id: string;
  board_id: string;
  title: string;
  position: number;
  wip_limit: number | null;
  list_role: KanbanListRole;
  policies: string | null;
  is_archived: number; // 0 | 1
  created_at: number;
  created_by: string;
  updated_at: number | null;
  updated_by: string | null;
}

export interface KanbanSwimlane {
  id: string;
  board_id: string;
  title: string;
  position: number;
  color: string | null;
  is_default: number; // 0 | 1
  is_collapsed: number; // 0 | 1
  created_at: number;
  created_by: string;
  updated_at: number | null;
  updated_by: string | null;
}

export interface KanbanCard {
  id: string;
  list_id: string;
  board_id: string;
  swimlane_id: string | null;
  title: string;
  description: string | null;
  position: number;
  priority: number; // 1–10
  card_type: KanbanCardType;
  class_of_service: KanbanClassOfService;
  due_date: number | null;
  start_date: number | null;
  completed_at: number | null;
  is_blocked: number; // 0 | 1
  blocked_reason: string | null;
  is_archived: number; // 0 | 1
  cover_color: string | null;
  cover_image_url: string | null;
  list_entered_at: number;
  metadata: string | null;
  created_at: number;
  created_by: string;
  updated_at: number | null;
  updated_by: string | null;
}

export interface KanbanAssignee {
  card_id: string;
  user_uuid: string;
  assigned_by: string;
  assigned_at: number;
}

export interface KanbanLabel {
  id: string;
  board_id: string;
  name: string;
  color: string;
  description: string | null;
  created_at: number;
  created_by: string;
}

export interface KanbanChecklist {
  id: string;
  card_id: string;
  title: string;
  position: number;
  created_at: number;
  created_by: string;
  updated_at: number | null;
  updated_by: string | null;
  items: KanbanChecklistItem[];
}

export interface KanbanChecklistItem {
  id: string;
  checklist_id: string;
  content: string;
  is_checked: number; // 0 | 1
  position: number;
  assigned_to: string | null;
  due_date: number | null;
  checked_by: string | null;
  checked_at: number | null;
  created_at: number;
  created_by: string;
}

export interface KanbanComment {
  id: string;
  card_id: string;
  content: string;
  author_id: string;
  created_at: number;
  updated_at: number | null;
  updated_by: string | null;
}

export interface KanbanAttachment {
  id: string;
  card_id: string;
  r2_key: string;
  filename: string;
  content_type: string;
  size: number;
  uploader_id: string;
  created_at: number;
}

export interface KanbanWatcher {
  card_id: string;
  user_uuid: string;
  watched_at: number;
}

export interface KanbanCardLink {
  id: string;
  card_id: string;
  link_type: KanbanCardLinkType;
  link_target_id: string;
  notes: string | null;
  linked_by: string;
  linked_at: number;
}

export interface KanbanCardHistory {
  id: string;
  card_id: string;
  board_id: string;
  from_list_id: string | null;
  to_list_id: string;
  from_swimlane_id: string | null;
  to_swimlane_id: string | null;
  moved_by: string;
  moved_at: number;
}

export interface KanbanCadence {
  id: string;
  board_id: string;
  title: string;
  cadence_type: KanbanCadenceType;
  frequency: KanbanCadenceFrequency;
  interval_days: number | null;
  next_occurrence: number | null;
  last_occurrence: number | null;
  calendar_event_id: string | null;
  description: string | null;
  is_active: number; // 0 | 1
  created_at: number;
  created_by: string;
  updated_at: number | null;
  updated_by: string | null;
}

export interface KanbanBoardSetting {
  board_id: string;
  setting_key: string;
  setting_value: string;
  updated_at: number;
  updated_by: string;
}

export interface KanbanActivityLogEntry {
  id: string;
  board_id: string;
  card_id: string | null;
  list_id: string | null;
  action: string;
  description: string;
  old_value: string | null;
  new_value: string | null;
  actor_id: string;
  created_at: number;
}

// ── Composite / Full Types ──────────────────────────────────────────────────

export interface KanbanChecklistProgress {
  total: number;
  checked: number;
}

/** Card summary as returned in the full board payload */
export interface KanbanCardSummary extends KanbanCard {
  labels: Pick<KanbanLabel, 'id' | 'name' | 'color'>[];
  assignees: Pick<KanbanAssignee, 'user_uuid' | 'assigned_at'>[];
  checklist_progress: KanbanChecklistProgress;
  comment_count: number;
  attachment_count: number;
  watcher_count: number;
  link_count: number;
  /** Present when the board payload embeds outbound links (used to stack subtasks on the board). */
  links?: KanbanCardLink[];
}

/** Full card with all relations as returned by GET /kanban/cards/{id} */
export interface KanbanCardFull extends KanbanCard {
  labels: KanbanLabel[];
  assignees: KanbanAssignee[];
  checklists: KanbanChecklist[];
  comments: KanbanComment[];
  attachments: KanbanAttachment[];
  watchers: KanbanWatcher[];
  links: KanbanCardLink[];
}

/** Complete board state from GET /kanban/boards/{id}/full */
export interface KanbanFullBoard {
  board: KanbanBoard;
  lists: KanbanList[];
  cards: KanbanCardSummary[];
  labels: KanbanLabel[];
  swimlanes: KanbanSwimlane[];
  settings: KanbanBoardSetting[];
}

/** Board with lists and labels from GET /kanban/boards/{id} */
export interface KanbanBoardWithDetails extends KanbanBoard {
  lists: (KanbanList & { card_count: number })[];
  labels: Pick<KanbanLabel, 'id' | 'name' | 'color' | 'description'>[];
}

/** Upcoming cadence with board info */
export interface KanbanUpcomingCadence extends KanbanCadence {
  board_title: string;
  team: string;
}

// ── Metrics Types ───────────────────────────────────────────────────────────

export interface KanbanLeadCycleTimeMetrics {
  avg_ms: number;
  p50_ms: number;
  p85_ms: number;
  p95_ms: number;
  sample_size: number;
}

export interface KanbanThroughputDataPoint {
  date: string;
  count: number;
}

export interface KanbanThroughputMetrics {
  granularity: 'day' | 'week' | 'month';
  data: KanbanThroughputDataPoint[];
  total: number;
}

export interface KanbanWIPAgingEntry {
  card_id: string;
  title: string;
  list_id: string;
  list_title: string;
  age_ms: number;
  class_of_service: KanbanClassOfService;
  priority: number;
  is_blocked: number;
}

export interface KanbanBottleneckEntry {
  list_id: string;
  list_title: string;
  position: number;
  card_count: number;
  wip_limit: number | null;
  is_over_limit: boolean;
  avg_wip_age_ms: number;
}

export interface KanbanCFDEntry {
  date: string;
  list_id: string;
  list_title: string;
  card_count: number;
  wip_age_avg_ms: number;
}

export interface KanbanMetricsSummary {
  leadTime: KanbanLeadCycleTimeMetrics;
  cycleTime: KanbanLeadCycleTimeMetrics;
  throughput: KanbanThroughputMetrics;
  wipAging: KanbanWIPAgingEntry[];
  bottlenecks: KanbanBottleneckEntry[];
  cfd: KanbanCFDEntry[];
}

// ── Stats Types ─────────────────────────────────────────────────────────────

export interface KanbanStats {
  total_boards: number;
  total_lists: number;
  total_cards: number;
  total_archived_cards: number;
  cards_by_priority: Record<string, number>;
  cards_by_type: Record<string, number>;
  cards_by_class: Record<string, number>;
  cards_overdue: number;
  cards_blocked: number;
  cards_completed: number;
  total_comments: number;
  total_attachments: number;
  active_members: number;
}

// ── Paginated Response ──────────────────────────────────────────────────────

export interface KanbanPaginatedResponse<T> {
  page: number;
  limit: number;
  total: number;
  results: T[];
}

// ── Request Types ───────────────────────────────────────────────────────────

export interface CreateBoardRequest {
  team: string;
  title: string;
  description?: string;
  background_color?: string;
}

export interface UpdateBoardRequest {
  title?: string;
  description?: string | null;
  background_color?: string | null;
}

export interface CreateListRequest {
  title: string;
  position?: number;
  wip_limit?: number | null;
  list_role?: KanbanListRole;
  policies?: string;
}

export interface UpdateListRequest {
  title?: string;
  wip_limit?: number | null;
  list_role?: KanbanListRole;
  policies?: string | null;
}

export interface CreateSwimlaneRequest {
  title: string;
  position?: number;
  color?: string;
  is_default?: boolean;
}

export interface UpdateSwimlaneRequest {
  title?: string;
  color?: string | null;
  is_default?: boolean;
}

export interface CreateCardRequest {
  list_id: string;
  swimlane_id?: string | null;
  title: string;
  description?: string;
  position?: number;
  priority?: number;
  card_type?: KanbanCardType;
  class_of_service?: KanbanClassOfService;
  due_date?: number | null;
  start_date?: number | null;
  /** If true, server may set blocked state on create; otherwise use `blockCard` after create. */
  is_blocked?: boolean;
  blocked_reason?: string | null;
  cover_color?: string | null;
  cover_image_url?: string | null;
  metadata?: string | null;
}

export interface UpdateCardRequest {
  title?: string;
  description?: string | null;
  priority?: number;
  card_type?: KanbanCardType;
  class_of_service?: KanbanClassOfService;
  due_date?: number | null;
  start_date?: number | null;
  completed_at?: number | null;
  cover_color?: string | null;
  cover_image_url?: string | null;
  metadata?: string | null;
}

export interface MoveCardRequest {
  list_id?: string;
  swimlane_id?: string | null;
  position?: number;
}

export interface BatchMoveRequest {
  card_ids: string[];
  list_id: string;
  swimlane_id?: string | null;
}

export interface BlockCardRequest {
  is_blocked: boolean;
  blocked_reason?: string | null;
}

export interface CreateLabelRequest {
  name: string;
  color: string;
  description?: string;
}

export interface UpdateLabelRequest {
  name?: string;
  color?: string;
  description?: string | null;
}

export interface CreateChecklistRequest {
  title?: string;
  position?: number;
}

export interface UpdateChecklistRequest {
  title?: string;
}

export interface CreateChecklistItemRequest {
  content: string;
  position?: number;
  assigned_to?: string | null;
  due_date?: number | null;
}

export interface UpdateChecklistItemRequest {
  content?: string;
  assigned_to?: string | null;
  due_date?: number | null;
  position?: number;
}

export interface CreateCommentRequest {
  content: string;
}

export interface UpdateCommentRequest {
  content: string;
}

export interface CreateCardLinkRequest {
  link_type: KanbanCardLinkType;
  link_target_id: string;
  notes?: string;
}

export interface CreateCadenceRequest {
  title: string;
  cadence_type: KanbanCadenceType;
  frequency: KanbanCadenceFrequency;
  interval_days?: number | null;
  description?: string | null;
  next_occurrence?: number | null;
  calendar_event_id?: string | null;
  is_active?: boolean;
}

export interface UpdateCadenceRequest {
  title?: string;
  cadence_type?: KanbanCadenceType;
  description?: string | null;
  frequency?: KanbanCadenceFrequency;
  interval_days?: number | null;
  next_occurrence?: number | null;
  last_occurrence?: number | null;
  calendar_event_id?: string | null;
  is_active?: boolean;
}

export interface SetBoardSettingRequest {
  setting_value: string;
}

// ── Query / Filter Params ───────────────────────────────────────────────────

export interface ListBoardsParams {
  team?: string;
  includeArchived?: boolean;
  page?: number;
  limit?: number;
}

export interface ListCardsParams {
  list_id?: string;
  swimlane_id?: string;
  priority_min?: number;
  priority_max?: number;
  card_type?: KanbanCardType;
  class_of_service?: KanbanClassOfService;
  is_blocked?: boolean;
  is_archived?: boolean;
  search?: string;
  sort_by?: 'created_at' | 'priority' | 'position' | 'due_date' | 'updated_at';
  sort_order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface CardCountByList {
  list_id: string;
  list_title: string;
  card_count: number;
}

// ── Board Template Types ────────────────────────────────────────────────────

export interface BoardTemplateList {
  title: string;
  list_role: KanbanListRole;
  wip_limit?: number;
  position: number;
}

/** Example card shown when applying a board template (optional seed content). */
export interface BoardTemplateExampleCard {
  /** Index into the template's `lists` array (after sort by position). */
  listIndex: number;
  title: string;
  description?: string;
  card_type?: KanbanCardType;
  class_of_service?: KanbanClassOfService;
  priority?: number;
  /** Unix ms */
  due_date?: number | null;
  start_date?: number | null;
  is_blocked?: boolean;
  blocked_reason?: string | null;
  cover_color?: string | null;
  checklists?: { title: string; items: { content: string; is_checked?: boolean }[] }[];
}

export interface BoardTemplate {
  name: string;
  /** Suggested team when this template is selected (user may pick another). */
  team: string;
  lists: BoardTemplateList[];
  exampleCards?: BoardTemplateExampleCard[];
}

const weekFromNow = () => Date.now() + 7 * 86400000;
const twoWeeksFromNow = () => Date.now() + 14 * 86400000;
const threeDaysFromNow = () => Date.now() + 3 * 86400000;

export const BOARD_TEMPLATES: BoardTemplate[] = [
  {
    name: 'Dev Team',
    team: 'devteam',
    lists: [
      { title: 'Backlog', list_role: 'backlog', position: 0 },
      { title: 'To Do', list_role: 'commitment_point', position: 1 },
      { title: 'In Progress', list_role: 'in_progress', wip_limit: 5, position: 2 },
      { title: 'Review', list_role: 'in_progress', wip_limit: 3, position: 3 },
      { title: 'Done', list_role: 'delivery_point', position: 4 },
      { title: 'Released', list_role: 'done', position: 5 },
    ],
    exampleCards: [
      {
        listIndex: 0,
        title: 'Showcase: Bug + Expedite + due date + checklist bar',
        card_type: 'bug',
        class_of_service: 'expedite',
        priority: 2,
        due_date: threeDaysFromNow(),
        description:
          '**Card type** changes the colored tag on the mini card. **Class of service** adds Expedite styling (urgent lane signal). **Due date** shows as a relative line under the card. **Checklists** fill the progress bar on the mini card; open the card for full checklist editing, reorder, and assignees on items if your workflow uses them.',
        checklists: [
          {
            title: 'What this checklist demonstrates',
            items: [
              { content: 'Checked row (counts toward the bar on the board)', is_checked: true },
              { content: 'Open row (still counts in total)' },
              { content: 'Another open row (shows multi step work inside one card)' },
            ],
          },
        ],
      },
      {
        listIndex: 0,
        title: 'Showcase: Story + Standard (big picture backlog item)',
        card_type: 'story',
        class_of_service: 'standard',
        priority: 7,
        description:
          '**Story** is for narrative scope: user journeys, outcomes, or themes that later split into tasks. Keep backlog cards readable: short title, longer explanation lives here in markdown. **Priority** uses the badge when it is not the default middle value.',
      },
      {
        listIndex: 1,
        title: 'Showcase: Feature + Standard + commitment column',
        card_type: 'feature',
        class_of_service: 'standard',
        priority: 5,
        due_date: weekFromNow(),
        description:
          'Use **Feature** when the work is product facing. Columns with **list roles** (commitment, in progress, delivery) help your team agree where work is truly promised vs still optional. Drag cards between lists to change state; WIP limits on columns warn when a stage is overloaded.',
        checklists: [
          {
            title: 'Definition of ready (template)',
            items: [
              { content: 'Rename these lines to match your team contract', is_checked: true },
              { content: 'Add acceptance bullets your reviewers expect' },
            ],
          },
        ],
      },
      {
        listIndex: 1,
        title: 'Showcase: Epic + Fixed date (calendar driven slice)',
        card_type: 'epic',
        class_of_service: 'fixed_date',
        priority: 3,
        due_date: twoWeeksFromNow(),
        start_date: Date.now() - 3 * 86400000,
        description:
          '**Epic** signals a larger body of work. **Fixed date** class adds the orange border cue for hard deadlines. **Start date** and **due date** both appear in the card panel timeline area so planners see duration at a glance.',
      },
      {
        listIndex: 2,
        title: 'Showcase: Task + Intangible (spikes, docs, enablement)',
        card_type: 'task',
        class_of_service: 'intangible',
        priority: 8,
        description:
          '**Intangible** uses a dashed border so enablement work does not look like shippable features. Good for refactors, research, or policy. Pair with a low numeric **priority** if you want it to sort differently in filtered views.',
      },
      {
        listIndex: 2,
        title: 'Showcase: Blocked work (stripe + reason on the board)',
        card_type: 'task',
        class_of_service: 'standard',
        priority: 4,
        is_blocked: true,
        blocked_reason: 'Replace with a dependency a human can act on',
        description:
          '**Blocked** is a first class field: the mini card shows a red stripe and the reason text. Use it when work cannot continue until something external resolves. Clear the block from the card detail panel when unblocked.',
      },
      {
        listIndex: 2,
        title: 'Showcase: Subtask type + cover color strip',
        card_type: 'subtask',
        class_of_service: 'standard',
        priority: 6,
        cover_color: '#3498DB',
        description:
          '**Subtask** is another type tag for smaller work items. **Cover color** draws a thin bar on the mini card so teams can color code lanes, owners, systems, or risk. Pick any hex from the card editor.',
      },
      {
        listIndex: 3,
        title: 'Showcase: Improvement + Review column',
        card_type: 'improvement',
        class_of_service: 'standard',
        priority: 1,
        description:
          '**Improvement** tags tech health or quality work. **Priority 1** shows the urgent priority chip. Review lists are ideal for peer review: keep acceptance criteria in the description so reviewers do not hunt chat logs.',
      },
      {
        listIndex: 3,
        title: 'Showcase: Other type + highest priority chip',
        card_type: 'other',
        class_of_service: 'standard',
        priority: 10,
        description:
          '**Other** is a neutral catch all when none of the specialized types fit. **Priority 10** is the calmest band on the mini card (P1 is most urgent, P10 is least). Adjust priorities to match how your team sorts work.',
      },
      {
        listIndex: 4,
        title: 'Showcase: Delivery column (list role delivery_point)',
        card_type: 'task',
        class_of_service: 'standard',
        priority: 5,
        description:
          'This list uses the **delivery_point** role in the template. Use it for work that is finished from the team perspective but not yet archived. Your admin can rename lists; roles drive metrics and hints in some views.',
      },
      {
        listIndex: 5,
        title: 'Showcase: Done list (list role done)',
        card_type: 'task',
        class_of_service: 'standard',
        priority: 5,
        description:
          'The last column uses the **done** list role. Cards here are typically completed work you still want visible for a while. Archive or delete sample cards once the board is live. **Links** on cards (notes, incidents, players, etc.) are managed from the card detail panel, same linking flow as elsewhere in ARGUS.',
      },
    ],
  },
  {
    name: 'Build Team',
    team: 'buildteam',
    lists: [
      { title: 'Ideas', list_role: 'backlog', position: 0 },
      { title: 'Planned', list_role: 'commitment_point', position: 1 },
      { title: 'Building', list_role: 'in_progress', wip_limit: 4, position: 2 },
      { title: 'Review', list_role: 'in_progress', wip_limit: 2, position: 3 },
      { title: 'Completed', list_role: 'delivery_point', position: 4 },
    ],
    exampleCards: [
      {
        listIndex: 0,
        title: 'Showcase: Ideas column (story + intangible)',
        card_type: 'story',
        class_of_service: 'intangible',
        priority: 7,
        description:
          '**Ideas** maps to a backlog style column in this template. **Intangible** keeps dashed styling so brainstorm work does not look like committed delivery. When an idea becomes real work, change type, class, and move it right.',
      },
      {
        listIndex: 0,
        title: 'Showcase: Bug + Expedite in early funnel',
        card_type: 'bug',
        class_of_service: 'expedite',
        priority: 2,
        due_date: threeDaysFromNow(),
        description:
          'Even in Ideas you can show **Expedite** if something must jump the queue. **Due dates** surface on the mini card so scanners see time pressure without opening every row.',
      },
      {
        listIndex: 1,
        title: 'Showcase: Planned + Feature + fixed date + checklist',
        card_type: 'feature',
        class_of_service: 'fixed_date',
        priority: 4,
        due_date: twoWeeksFromNow(),
        start_date: Date.now() - 2 * 86400000,
        description:
          '**Planned** is your commitment friendly column: work you intend to do soon. **Fixed date** plus **start** and **due** show how timeline fields look together. Checklists model a gate before moving to Building.',
        checklists: [
          {
            title: 'Gate before build (rename for your team)',
            items: [
              { content: 'Owner named', is_checked: true },
              { content: 'Scope written in this description' },
              { content: 'Dependencies listed or marked blocked elsewhere' },
            ],
          },
        ],
      },
      {
        listIndex: 1,
        title: 'Showcase: Epic card (large slice of work)',
        card_type: 'epic',
        class_of_service: 'standard',
        priority: 5,
        description:
          '**Epic** is the purple type tag. Use it when one card represents a bucket that will spawn many smaller cards later. Link related cards from the detail panel when cross references matter.',
      },
      {
        listIndex: 2,
        title: 'Showcase: Building + cover color + standard task',
        card_type: 'task',
        class_of_service: 'standard',
        priority: 5,
        cover_color: '#E67E22',
        description:
          '**Cover color** is a visual grouping tool (owner, modpack lane, risk). **WIP limits** on this column show counts vs limit in the list header; dragging extra cards in should feel visually heavy when you are over limit.',
      },
      {
        listIndex: 2,
        title: 'Showcase: Blocked in active build lane',
        card_type: 'task',
        class_of_service: 'standard',
        priority: 3,
        is_blocked: true,
        blocked_reason: 'Example: waiting on assets or sign off',
        description:
          'Blocked cards stay visible in **Building** so the column count reflects reality. The reason should answer "who or what unblocks this?" for the next shift.',
      },
      {
        listIndex: 2,
        title: 'Showcase: Subtask + improvement pair',
        card_type: 'subtask',
        class_of_service: 'standard',
        priority: 6,
        description:
          '**Subtask** tags fine grained execution. Pair with **Improvement** cards when you split polish from net new build work.',
      },
      {
        listIndex: 3,
        title: 'Showcase: Review + improvement + priority spread',
        card_type: 'improvement',
        class_of_service: 'standard',
        priority: 1,
        description:
          '**Review** is for eyes on quality. **Priority 1** shows the high urgency chip. Use markdown in descriptions for screenshots, links, and acceptance lists.',
      },
      {
        listIndex: 3,
        title: 'Showcase: Other type + lowest urgency',
        card_type: 'other',
        class_of_service: 'standard',
        priority: 10,
        description:
          '**Other** plus **priority 10** shows the least urgent band (blue chip). Compare with **priority 1** cards in this template for the red urgent chip.',
      },
      {
        listIndex: 4,
        title: 'Showcase: Completed column (delivery_point role)',
        card_type: 'task',
        class_of_service: 'standard',
        priority: 5,
        description:
          'This column uses **delivery_point** in the template. It is a good home for work the team agrees is done but you still want on the board for demos or reporting.',
      },
    ],
  },
  {
    name: 'Event Team',
    team: 'eventteam',
    lists: [
      { title: 'Ideas', list_role: 'backlog', position: 0 },
      { title: 'Planning', list_role: 'commitment_point', position: 1 },
      { title: 'Preparing', list_role: 'in_progress', wip_limit: 4, position: 2 },
      { title: 'Ready', list_role: 'in_progress', position: 3 },
      { title: 'Live', list_role: 'delivery_point', position: 4 },
      { title: 'Post-Event', list_role: 'done', position: 5 },
    ],
    exampleCards: [
      {
        listIndex: 0,
        title: 'Showcase: Ideas + story + intangible',
        card_type: 'story',
        class_of_service: 'intangible',
        priority: 7,
        description:
          'Events start fuzzy. **Ideas** holds possibilities. **Intangible** styling reminds everyone this is not a hard commitment yet. Rename lists to match your org language without losing the role based flow.',
      },
      {
        listIndex: 0,
        title: 'Showcase: Ideas + other + default priority',
        card_type: 'other',
        class_of_service: 'standard',
        priority: 5,
        description:
          '**Other** is the escape hatch type when nothing else fits. Keep titles short; long context belongs in markdown here.',
      },
      {
        listIndex: 1,
        title: 'Showcase: Planning + epic + fixed date + checklist',
        card_type: 'epic',
        class_of_service: 'fixed_date',
        due_date: weekFromNow(),
        start_date: Date.now() - 1 * 86400000,
        description:
          '**Planning** is where dates and scope tighten. **Epic** plus **Fixed date** shows the orange class cue with a big title. Checklists break the plan into visible steps on the mini card.',
        checklists: [
          {
            title: 'Run of show skeleton',
            items: [
              { content: 'Owners per stream', is_checked: true },
              { content: 'Comms tree draft' },
              { content: 'Rollback or rain plan noted' },
            ],
          },
        ],
      },
      {
        listIndex: 1,
        title: 'Showcase: Planning + feature + standard',
        card_type: 'feature',
        class_of_service: 'standard',
        priority: 4,
        description:
          '**Feature** tags audience facing deliverables: web, broadcast, merch, whatever your event uses. Move this card right when the team agrees it is truly in motion.',
      },
      {
        listIndex: 2,
        title: 'Showcase: Preparing + bug + expedite + due date',
        card_type: 'bug',
        class_of_service: 'expedite',
        priority: 2,
        due_date: threeDaysFromNow(),
        description:
          'Use **Preparing** for execution risk. **Bug** plus **Expedite** is the strongest visual combo: red type tag plus expedite border. Good for "this will hurt attendees if we do not fix it."',
      },
      {
        listIndex: 2,
        title: 'Showcase: Preparing + task + cover color',
        card_type: 'task',
        class_of_service: 'standard',
        priority: 5,
        cover_color: '#2ECC71',
        description:
          '**Cover color** can mean stage, venue, or stream. It is cosmetic but powerful for scanning a busy board during show week.',
      },
      {
        listIndex: 3,
        title: 'Showcase: Ready + blocked + reason',
        card_type: 'task',
        class_of_service: 'standard',
        priority: 3,
        is_blocked: true,
        blocked_reason: 'Example: sign off pending from stakeholder',
        description:
          '**Ready** should mean "we could go live with short notice." If something is still uncertain, **blocked** communicates that without hiding the card in a private chat.',
      },
      {
        listIndex: 3,
        title: 'Showcase: Ready + improvement + high priority',
        card_type: 'improvement',
        class_of_service: 'standard',
        priority: 1,
        description:
          '**Improvement** covers safety, accessibility, or polish. **Priority 1** surfaces it to anyone sorting by urgency.',
      },
      {
        listIndex: 4,
        title: 'Showcase: Live column (delivery_point)',
        card_type: 'task',
        class_of_service: 'standard',
        priority: 5,
        description:
          '**Live** uses **delivery_point** in the template: work that is actively in front of players or attendees. Use descriptions for runbook links and on call names.',
      },
      {
        listIndex: 5,
        title: 'Showcase: Post event + subtask + intangible wrap up',
        card_type: 'subtask',
        class_of_service: 'intangible',
        priority: 8,
        description:
          '**Post Event** maps to **done** style follow up. **Subtask** plus **Intangible** models retros, metrics pulls, or thank you notes that are real but not shippable features.',
      },
    ],
  },
  {
    name: 'Custom (Empty)',
    team: '',
    lists: [
      { title: 'Backlog', list_role: 'backlog', position: 0 },
      { title: 'In Progress', list_role: 'in_progress', position: 1 },
      { title: 'Done', list_role: 'done', position: 2 },
    ],
    exampleCards: [
      {
        listIndex: 0,
        title: 'Showcase: Backlog + story + dates + checklist',
        card_type: 'story',
        class_of_service: 'standard',
        priority: 6,
        due_date: weekFromNow(),
        start_date: Date.now() - 1 * 86400000,
        description:
          'This template only has three lists so you can grow slowly. **Backlog** holds ideas. **Start** and **due** dates show on the card detail timeline. Checklists show progress on the mini card without splitting work too early.',
        checklists: [
          {
            title: 'Board setup tour',
            items: [
              { content: 'Drag a card between lists (same as larger templates)', is_checked: true },
              { content: 'Open a card to try labels, links, comments, attachments' },
              { content: 'Drag a card to the trash drop zone to delete (with confirm)' },
            ],
          },
        ],
      },
      {
        listIndex: 0,
        title: 'Showcase: Backlog + epic + intangible',
        card_type: 'epic',
        class_of_service: 'intangible',
        priority: 7,
        description:
          '**Epic** plus **Intangible** is a common pattern for research or big themes before you commit dates. Change class to **Fixed date** when the calendar becomes real.',
      },
      {
        listIndex: 0,
        title: 'Showcase: Backlog + feature + expedite',
        card_type: 'feature',
        class_of_service: 'expedite',
        priority: 2,
        due_date: threeDaysFromNow(),
        description:
          'Even on a tiny board you can show **Expedite** and a **due date** together. Use when leadership needs visibility on a short fuse item.',
      },
      {
        listIndex: 1,
        title: 'Showcase: In progress + bug + blocked',
        card_type: 'bug',
        class_of_service: 'standard',
        priority: 3,
        is_blocked: true,
        blocked_reason: 'Example: need repro steps from reporter',
        description:
          '**In Progress** is where WIP discipline matters. **Blocked** keeps the card in the lane so counts stay honest. Pair **bug** type with clear repro notes in markdown.',
      },
      {
        listIndex: 1,
        title: 'Showcase: In progress + improvement + cover color',
        card_type: 'improvement',
        class_of_service: 'standard',
        priority: 4,
        cover_color: '#9B59B6',
        description:
          '**Improvement** for refactors or reliability. **Cover color** helps teammates find "purple lane" work in a hurry.',
      },
      {
        listIndex: 1,
        title: 'Showcase: In progress + subtask + other + priority spread',
        card_type: 'subtask',
        class_of_service: 'standard',
        priority: 10,
        description:
          '**Subtask** for small execution. **Priority 10** is the least urgent band on the badge. **Other** type is available when vocabulary does not fit; rename title to teach your convention.',
      },
      {
        listIndex: 2,
        title: 'Showcase: Done list + task + fixed date',
        card_type: 'task',
        class_of_service: 'fixed_date',
        priority: 5,
        due_date: weekFromNow(),
        description:
          '**Done** list role still allows cards with **Fixed date** if you track retro deadlines or cleanup windows. Archive cards when history no longer helps.',
      },
    ],
  },
];

// ── Display Helpers ─────────────────────────────────────────────────────────

export const CARD_TYPE_CONFIG: Record<KanbanCardType, { label: string; color: string }> = {
  bug: { label: 'Bug', color: '#E74C3C' },
  feature: { label: 'Feature', color: '#3498DB' },
  task: { label: 'Task', color: '#95A5A6' },
  story: { label: 'Story', color: '#2ECC71' },
  improvement: { label: 'Improvement', color: '#1ABC9C' },
  epic: { label: 'Epic', color: '#9B59B6' },
  subtask: { label: 'Subtask', color: '#BDC3C7' },
  other: { label: 'Other', color: '#7F8C8D' },
};

export const CLASS_OF_SERVICE_CONFIG: Record<KanbanClassOfService, { label: string; color: string; border: string }> = {
  expedite: { label: 'Expedite', color: '#E74C3C', border: 'border-red-500' },
  fixed_date: { label: 'Fixed Date', color: '#E67E22', border: 'border-orange-500' },
  standard: { label: 'Standard', color: '#95A5A6', border: 'border-transparent' },
  intangible: { label: 'Intangible', color: '#7F8C8D', border: 'border-dashed border-glass-300' },
};

export const LIST_ROLE_CONFIG: Record<KanbanListRole, { label: string; color: string }> = {
  backlog: { label: 'Backlog', color: '#95A5A6' },
  commitment_point: { label: 'Commitment', color: '#3498DB' },
  in_progress: { label: 'In Progress', color: '#F39C12' },
  delivery_point: { label: 'Delivery', color: '#2ECC71' },
  done: { label: 'Done', color: '#27AE60' },
  none: { label: 'None', color: '#7F8C8D' },
};

export const CADENCE_TYPE_CONFIG: Record<KanbanCadenceType, { label: string; color: string }> = {
  standup: { label: 'Standup', color: '#3498DB' },
  review: { label: 'Review', color: '#2ECC71' },
  retrospective: { label: 'Retrospective', color: '#9B59B6' },
  planning: { label: 'Planning', color: '#E67E22' },
  replenishment: { label: 'Replenishment', color: '#1ABC9C' },
  custom: { label: 'Custom', color: '#95A5A6' },
};

export const LINK_TYPE_CONFIG: Record<KanbanCardLinkType, { label: string; route?: string }> = {
  note: { label: 'Note', route: '/notes-warnings' },
  warning: { label: 'Warning', route: '/notes-warnings' },
  ban: { label: 'Ban', route: '/bans' },
  incident: { label: 'Incident', route: '/incidents' },
  calendar_event: { label: 'Calendar Event', route: '/calendar' },
  ticket: { label: 'Ticket' },
  partnership: { label: 'Partnership', route: '/partnerships' },
  partnership_creator: { label: 'Creator', route: '/partnerships' },
  partnership_modpack: { label: 'Modpack', route: '/partnerships' },
  build: { label: 'Build', route: '/build-team/builds' },
  build_project: { label: 'Project', route: '/build-team/projects' },
  build_team_member: { label: 'Team Member' },
  event: { label: 'Event', route: '/event-team/events' },
  event_project: { label: 'Event Project', route: '/event-team/projects' },
  event_team_member: { label: 'Event Team Member', route: '/event-team/members' },
  modpack_note: { label: 'Modpack Note', route: '/modpack-notes' },
  player: { label: 'Player', route: '/player' },
  evidence: { label: 'Evidence', route: '/evidence' },
  kanban_card: { label: 'Kanban Card', route: '/workspaces' },
  appeal: { label: 'Appeal', route: '/appeals' },
};
